<template>
    <div :id="rootElementId" :style="`height:${PITCH_HEIGHT}px`"></div>
</template>

<script>
import { getDataUrl, getNeutralColors } from '../../utils/functions';
import { loadFonts, copyAndAppendElement } from '../../utils/exportToPNG';
import ResizeObserver from 'resize-observer-polyfill';
import UserService from '../../services/user.service';
import * as d3v4 from "d3v4";
import Dictionary from '../../dictionary';

  export default {
    name: 'XTChart',
    props: {
      id:{
        type: String,
        required: true
      },
      size: {
        type: Number,
        required: false,
      },
      margin: {
        type: Object,
        required: false,
      },
      type : {
        type: String,
        required: true,
      },
      plays: {
        type: Array,
        required: true,
      },
      typeOfPlay: {
        type: Array,
        required: true,
      },
      minXT: {
        type: Number,
        required: true,
      },
      maxXT: {
        type: Number,
        required: true,
      },
      playerName: {
        type: String,
        required: false
      },
      seasons: {
        type: Array,
        required: false
      },
      game: {
        type: Object,
        required: false
      },
      player: {
        type: Object,
        required: false
      },
      circlesAndBoxes: {
        type: Array,
        required: false
      },
      language: {
        type: String,
        required: false
      },
      homeFillColor:{
        type: String,
        default: "#ffffff"
      },
      awayFillColor:{
        type: String,
        default: "#ffffff"
      },
      mediaMode: {
        type: Boolean,
        default: false
      },
      homeBorderColor:{
        type: String,
        default: "#000000"
      },
      awayBorderColor:{
        type: String,
        default: "#000000"
      },
      homeTeamName:{
        type: String,
        default: ""
      },
      awayTeamName:{
        type: String,
        default: ""
      },
      //gameId for print one game Player xG Map
      gameId:{
        type:Number,
        default: 0
      },
      gameName:{
        type: String,
        default: ""
      },
      fontFamily:{
        type: String,
        default: 'NeueMontreal'
      },
      minsPlayed: {
        type: Number,
        default: 0,
      },
    },
    data() {
      return {
        height: 0,
        width: 0,
        images: {}
      }
    },
    computed: {
      SVG_WIDTH(){
        return this.size ? this.size : 380;
      },
      PITCH_WIDTH(){
        if(this.margin){
          return this.SVG_WIDTH - this.margin.left - this.margin.right
        }else{
          return this.SVG_WIDTH;
        }
      },
      PITCH_HEIGHT() {
        return this.game ? this.PITCH_WIDTH * 0.67125 : this.PITCH_WIDTH * 0.755
      },
      rootElementId(){
        return `pitchXT${this.id}`
      },
      currentUserFieldColor() {
        return this.$store.getters["auth/fieldColor"];
      }
    },
    async mounted() {
      this.observer = new ResizeObserver(this.onResize).observe(document.getElementById(this.rootElementId));

      const SVG_WIDTH = this.size ? this.size : 380;
      const margin = this.margin ? this.margin : {top: 0, right: 0, bottom: 0, left: 0};

      const PITCH_WIDTH = this.PITCH_WIDTH;
      const PITCH_HEIGHT = this.PITCH_HEIGHT;

      d3.selectAll('.tooltip').remove();

      const tooltip = d3
        .select("body")
        .append("div")
        .attr("class", "tooltip");

      var svg = d3.select(`#${this.rootElementId}`)
        .append("svg")
        .attr("width", SVG_WIDTH)
        .attr("height", PITCH_HEIGHT)
        .style("background-color", "#ffffff")
        .attr("class", "pitch")
        .attr("id", `${this.rootElementId}SVG`);

      this.playsFiltered = this.plays.filter(s => s.xT >= this.minXT && s.xT <= this.maxXT)
      // DRAW PITCH
      this.drawImageBase64(svg, margin.left, margin.top, PITCH_WIDTH, PITCH_HEIGHT, require(`@/assets/img/${this.type === "game" ? "": "half_"}pitch_${this.currentUserFieldColor}.png`),'pitch',true);

      let neutralColorsHome = getNeutralColors('home');
      let firstHomeTeamColor = this.homeFillColor;
      let secondHomeTeamColor = this.type === "game" ? neutralColorsHome[1]  : "#000000";
      let neutralColorsAway = getNeutralColors('away');
      let firstAwayTeamColor = this.awayFillColor;
      let secondAwayTeamColor = this.type === "game" ? neutralColorsAway[1] : "#000000";
      if (this.type === "game" && !this.mediaMode) {
        firstHomeTeamColor = this.game.homeTeamGameColor ? this.game.homeTeamGameColor : (this.game.homeTeamColor ? this.game.homeTeamColor : firstHomeTeamColor);
        secondHomeTeamColor = this.game.homeTeamGameSecondColor ? this.game.homeTeamGameSecondColor : (this.game.homeTeamSecondColor ? this.game.homeTeamSecondColor : secondHomeTeamColor);
        firstAwayTeamColor = this.game.awayTeamGameColor ? this.game.awayTeamGameColor : (this.game.awayTeamColor ? this.game.awayTeamColor : firstAwayTeamColor);
        secondAwayTeamColor = this.game.awayTeamGameSecondColor ? this.game.awayTeamGameSecondColor : (this.game.awayTeamSecondColor ? this.game.awayTeamSecondColor : secondAwayTeamColor);
      }
      if(this.type === "game" && firstHomeTeamColor === firstAwayTeamColor){
        firstAwayTeamColor = neutralColorsAway[0];
      }
      if(this.type === "game" && secondHomeTeamColor === secondAwayTeamColor){
        secondAwayTeamColor = neutralColorsAway[1];
      }

      let logo  = require(`@/assets/img/driblab${this.mediaMode ? "_normal" : ""}_${this.currentUserFieldColor === 'white' ? 'color' : 'white'}.png`);
      // DRAW LOGO
      await this.drawImageBase64(svg,
        PITCH_WIDTH * 0.735 + margin.left,
        this.type === "game" ? PITCH_HEIGHT * 0.77 + margin.top : PITCH_HEIGHT * 0.785 + margin.top,
        PITCH_WIDTH * 0.18,
        PITCH_HEIGHT * 0.18,
        logo,
        'logo');

      if (this.type === "game") {
        this.renderXTPassSegments(svg, 1, PITCH_WIDTH, PITCH_HEIGHT, this.playsFiltered, firstHomeTeamColor, tooltip, this.type === "player");
        this.renderXTCircles(svg, 1, PITCH_WIDTH, PITCH_HEIGHT, this.playsFiltered, firstHomeTeamColor, firstHomeTeamColor, tooltip, this.type === "player");
        this.printGameXTLegend(svg, PITCH_WIDTH, margin, this.plays, this.minXT, this.typeOfPlay)
        this.printGameLegend(svg, PITCH_WIDTH, PITCH_HEIGHT, margin, firstHomeTeamColor, firstAwayTeamColor, this.game)
      } else {
        if (this.circlesAndBoxes && this.circlesAndBoxes.includes("boxes"))
          this.renderBoxes(svg,
            SVG_WIDTH,
            PITCH_HEIGHT,
            margin,
            this.plays,
            this.language
          )
        if (this.circlesAndBoxes && this.circlesAndBoxes.includes("circles")) {
          this.renderXTPassSegments(svg, 1, PITCH_WIDTH, PITCH_HEIGHT, this.playsFiltered, firstHomeTeamColor, tooltip, this.type === "player");
          this.renderXTCircles(svg, 1, PITCH_WIDTH, PITCH_HEIGHT, this.playsFiltered, firstHomeTeamColor, firstHomeTeamColor, tooltip, this.type === "player");
        }

        this.printPlayerXTLegend(svg, PITCH_WIDTH, margin, this.playsFiltered, this.seasons, this.minXT, this.typeOfPlay, this.playerName)
      }

      let font = await loadFonts(this.fontFamily);
      copyAndAppendElement(svg.node(),font.node(),0,0);

      //Emit when is loaded
      this.$emit("loaded", this.id);
    },
    methods: {
      measureText(string, fontSize = 10) {
        const widths = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0.278125,0.278125,0.35625,0.55625,
      		0.55625,0.890625,0.6671875,0.1921875,0.334375,0.334375,0.390625,0.584375,0.278125,0.334375,0.278125,0.303125,0.55625,
      		0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.55625,0.278125,0.278125,0.5859375,0.584375,0.5859375,
      		0.55625,1.015625,0.6671875,0.6671875,0.7234375,0.7234375,0.6671875,0.6109375,0.778125,0.7234375,0.278125,0.5,0.6671875,
      		0.55625,0.834375,0.7234375,0.778125,0.6671875,0.778125,0.7234375,0.6671875,0.6109375,0.7234375,0.6671875,0.9453125,
      		0.6671875,0.6671875,0.6109375,0.278125,0.35625,0.278125,0.478125,0.55625,0.334375,0.55625,0.55625,0.5,0.55625,
      		0.55625,0.278125,0.55625,0.55625,0.2234375,0.2421875,0.5,0.2234375,0.834375,0.55625,0.55625,0.55625,0.55625,
      		0.334375,0.5,0.278125,0.55625,0.5,0.7234375,0.5,0.5,0.5,0.35625,0.2609375,0.3546875,0.590625];
        const avg = 0.5293256578947368;

        if (!string)
          return 0

        return string
          .split('')
          .map(c => c.charCodeAt(0) < widths.length ? widths[c.charCodeAt(0)] : avg)
          .reduce((cur, acc) => acc + cur) * fontSize;
      },
      colorScale(minDomain, maxDomain, value, steps) {
          //Range of colors in HSV format gradient
          var colorsArray = ["#fb0e0e","#fb130e","#fb180f","#fa1d0f","#fa220f","#fa270f","#fa2c10","#fa3110","#f93610","#f93b11","#f94011","#f94511","#f84911","#f84e12","#f85312",
          "#f85812","#f85d13","#f76113","#f76613","#f76b13","#f76f14","#f77414","#f67914","#f67d14","#f68215","#f68715","#f58b15","#f59016","#f59416","#f59916","#f59d16","#f4a217",
          "#f4a617","#f4aa17","#f4af17","#f4b318","#f3b818","#f3bc18","#f3c019","#f3c519","#f3c919","#f2cd19","#f2d11a","#f2d61a","#f2da1a","#f1de1a","#f1e21b","#f1e61b","#f1ea1b",
          "#f1ee1b","#eef01c","#eaf01c","#e5f01c","#e1f01d","#dcf01d","#d8ef1d","#d4ef1d","#cfef1e","#cbef1e","#c6ee1e","#c2ee1e","#beee1f","#baee1f","#b5ee1f","#b1ed1f","#aded20",
          "#a9ed20","#a4ed20","#a0ed20","#9cec21","#98ec21","#94ec21","#90ec21","#8cec22","#88eb22","#84eb22","#80eb22","#7ceb23","#78ea23","#74ea23","#70ea23","#6cea24","#68ea24",
          "#64e924","#60e924","#5ce925","#58e925","#54e925","#51e825","#4de826","#49e826","#45e826","#42e726","#3ee727","#3ae727","#37e727","#33e727","#2fe628","#2ce628","#28e628"];

          //Get scale functions.
          // - Range: Number of gradient steps
          // - Domain: Range of values
          let colorScale = d3.scale.linear().domain([minDomain, maxDomain]).range([0, steps - 1]);
          //Get index of colorsArray
          var index = colorScale(value);
          //Get whole part of the index
          var color = colorsArray[Math.trunc(index)];

          return color;
      },
      dist(p1, x, y) {
        let dx = x - p1.x;
        let dy = y - p1.y;
        return Math.sqrt(dx * dx + dy * dy);
      },
      getLengthForPoint(x, y, thePath){
        const self = this;
        let pathLength = thePath.getTotalLength();
        let precision = 100;
        let division = pathLength / precision;

          let theRecord = pathLength;
          let theSegment;
          for (let i = 0; i < precision; i++) {
          // get a point on the path for thia distance
          let _p = thePath.getPointAtLength(i * division);
          // get the distance between the new point _p and the point p
          let theDistance = self.dist(_p, x, y);
          if (theDistance < theRecord) {
          // if the distance is smaller than the record set the new record
            theRecord = theDistance;
            theSegment = i;
          }
        }
        return(theSegment * division);
      },
      transition(path, points) {
        const self = this;
        path.transition()
            .duration(500)
            .attrTween("stroke-dasharray", function() {
              const distanceToPoint = self.getLengthForPoint(points[1][0], points[1][1], this);

              var l = this.getTotalLength(),
                  i = d3.interpolateString("0," + l, distanceToPoint + "," + l);
              return function(t) { return i(t); };
            })
            .each("end", function() { d3.select(this).call( function() { return null; }); });
      },
      renderXTPassSegments(svg, chartSizeRate, chartWidth, chartHeight, segments, teamColor, tooltip, isPlayerXT, isLegend = false) {

        let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([chartWidth, 0]);
        let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([chartHeight, 0]);
        // ====================================================================
        //        Render Segments
        // ====================================================================
        for (let segment of segments) {
          let sx0;
          let sy0;
          let sx1;
          let sy1;
          let sx2;
          let sy2;

          // THIS is a fix for when a carry comes from the own pitch
          // We cut the line to match the bottom of the SVG so that, when
          // downloading the PNG, the size of the SVG matches the size of the pitch image
          if (segment.x_start < 44.8 && isPlayerXT && !isLegend)
           segment.x_start = 44.8

          if (isPlayerXT) {
            sx0 = x(segment.carry ? segment.y_start : segment.y);
            sy0 = y(segment.carry ? 2 * segment.x_start - 100 : 2 * segment.x - 100);
            sx1 = x(segment.y);
            sy1 = y(2 * segment.x - 100);
            sx2 = x(segment.y_end);
            sy2 = y(2 * segment.x_end - 100);
          } else if (segment.homeTeam) {
            sx0 = x(segment.carry ? segment.x_start : segment.x);
            sy0 = y(segment.carry ? 100 - segment.y_start : 100 - segment.y);
            sx1 = x(segment.x);
            sy1 = y(100 - segment.y);
            sx2 = x(segment.x_end);
            sy2 = y(100 - segment.y_end);
          } else {
            sx0 = x(segment.carry ? 100 - segment.x_start : 100 - segment.x);
            sy0 = y(segment.carry ? segment.y_start : segment.y);
            sx1 = x(100 - segment.x);
            sy1 = y(segment.y);
            sx2 = x(100 - segment.x_end);
            sy2 = y(segment.y_end);
          }
          if (segment.type === "PASS") {
            this.drawCarryAndPassLine(svg, chartSizeRate, segment, sx0, sy0, sx1, sy1, sx2, sy2, chartWidth, segment.xT);

            var color = "green";
            var triangleSize = chartWidth * 0.04;
            const angleRadians = Math.atan2(sy2 - sy1, sx2 - sx1);

            var verticalTransform = (angleRadians + (Math.PI / 2)) * (180/Math.PI); // To 180 and from radians to degrees

            var triangle = d3.svg.symbol()
              .type("triangle-up")
              .size(triangleSize)

            svg.append("path")
            .attr("d", triangle)
            .attr("stroke", this.colorScale(0.4, 0, segment.xT > 0.4 ? 0.4 : segment.xT, 100))
            .attr("fill", this.colorScale(0.4, 0, segment.xT > 0.4 ? 0.4 : segment.xT, 100))
            .attr("transform", function(d) { return "translate(" + sx2 + "," + sy2 + ") rotate(" + verticalTransform + ")"; });

           } else {
             this.drawXTLine(svg, chartSizeRate, segment, sx0, sy0, sx1, sy1, chartWidth, segment.xT, tooltip, teamColor, 0.8, true);
           }
        }
      },
      async drawImageBase64(svg,x,y,width,height,img,idName,bottom = false) {
        return new Promise((resolve) => {
          if(!this.images[idName]){
            let base_image = new Image();
            base_image.crossOrigin = "anonymous";
            base_image.src = img;
            base_image.onload = async (event) => {
              const dataUrl = await getDataUrl(event.currentTarget);
              this.drawImage(
              svg,
              x,
              y,
              width,
              height,
              dataUrl,
              idName,
              bottom
              );
              resolve(true);
            }
          }else{
            if(bottom){
              svg.node().insertBefore(this.images[idName],svg.node().firstChild);
            }else{
              svg.node().appendChild(this.images[idName]);
            }
            resolve(true);
          }
        })
      },
      drawImage: function(svg, x, y, width, height, img,idName,bottom = false) {
        let imgNode = bottom ? svg.insert("svg:image",':first-child') : svg.append("svg:image");
          imgNode.attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .attr("xlink:href", img);
        this.images[idName] = imgNode.node().cloneNode(true);
      },
      drawRectangle(svg, x, y, width, height, color, opacity, className) {
        return svg.append("rect")
          .attr("class", className)
          .attr("x", x)
          .attr("y", y)
          .attr("width", width)
          .attr("height", height)
          .style("fill", color)
          .style("opacity", opacity);
      },
      writeText(svg, x, y, anchor, fontSize, text, fill = "white", className = "none") {
        svg.append("text")
          .attr("x", x)
          .attr("y", y)
          .attr("text-anchor", anchor)
          .attr("dy", "0.8em")
          .attr("font-weight", 500)
          .attr("class", className)
          .style("font-size", fontSize)
          .style("font-family", this.fontFamily)
          .style("fill", fill)
          .text(text);
      },
      getTypeOfEvent(name) {
        if (name === "PASS") return "Pass";
        if (name === "FOUL") return "Foul";
        if (name === "AERIAL") return "Aerial";
        if (name === "SAVED SHOT") return "Saved Shot";
        if (name === "GOAL") return "Goal";
        if (name === "SHOT ON POST") return "Shot on Post";
        if (name === "BALL RECOVERY") return "Ball Recovery";
        if (name === "INTERCEPTION") return "Interception";
        if (name === "TAKE ON") return "Dribble";
        if (name === "MISSED SHOT") return "Missed Shot";

      	return name;
      },
      isAshot(name) {
        if (name === "SAVED SHOT") return true;
        if (name === "GOAL") return true;
        if (name === "SHOT ON POST") return true;
        if (name === "MISSED SHOT") return true;
        return false;
      },
      drawCircle(svg, x, y, radius, fillColor,borderColor,borderSize, className) {
        return svg.append("circle")
          .attr("class", className)
          .attr("r", radius)
          .attr("cx", x)
          .attr("cy", y)
          .style("fill", fillColor)
          .style("stroke", borderColor)
          .style("stroke-width", radius / 5)
          .style("fill-opacity", 0.6)
          .style("opacity", 0.8);
      },
      drawXTCircle(svg, chartSizeRate, chartWidth, x, y, circle, i, radius, xt, tooltip) {
        var self = this;
        svg.append("circle")
          .attr("r", radius * chartSizeRate)
          .attr("cx", x)
          .attr("cy", y)
      		.attr('id', "pass" + i + "-" + circle.teamName + "-" + circle.playerName)
          .style("fill-opacity", 0.6)
          .style("opacity", 0.8)
          .style("stroke",  this.colorScale(0.4, 0, xt > 0.4 ? 0.4 : xt, 100))
          .style("stroke-width", 2 * chartWidth * 0.001)
          .style("fill", this.colorScale(0.4, 0, xt > 0.4 ? 0.4 : xt, 100))
      		.on('mouseenter',function(d) {
      			d3.select(this)
      				.transition()
      				.duration(300)
              .style("cursor", "pointer")
              .attr('r', radius * 1.5);

            var textLine1 = circle.game;
            var textLine2 = circle.strategy.endsWith("play") ? Dictionary[self.language]["game"]["gameThreat"][_.camelCase(circle.strategy)] : Dictionary[self.language]["game"]["gameThreat"][_.camelCase(circle.strategy + " play")];
            if (!textLine2)
              textLine2 = Dictionary[self.language]["game"]["gameThreat"]["regularPlay"]
            var textLine3 = "Carry and Pass ";
            if (self.isAshot(circle.type))
              textLine3 = "Shot ";
            else if (Math.abs(circle.x - circle.x_start) < 5 && Math.abs(circle.y - circle.y_start) < 5)
              textLine3 = "Pass ";
            else if (circle.type !== "PASS")
              textLine3 = "Carry ";

            textLine3 = Dictionary[self.language]["game"]["gameThreat"][_.camelCase('legend'+textLine3)]

            textLine3 = textLine3 + " at " +  + circle.min + ":" + (circle.sec < 10 ? "0" + circle.sec : circle.sec) +  " · <b>" + (circle.xT.toFixed(3) * 1) + " xT</b>";

            var textLine4 = self.getTypeOfEvent(circle.type) +  ( circle.type === "FOUL" ? " to " : " of ") + circle.player + ( circle.playerTo && !self.isAshot(circle.type) && circle.type !== "FOUL"  && circle.type !== "BALL RECOVERY" ? " to " + circle.playerTo : "");
            var maxTextWidth = Math.max(self.measureText(textLine1, 14), self.measureText(textLine2, 14), self.measureText(textLine3, 14), self.measureText(textLine4, 14));
            var widthTooltip = maxTextWidth + 60;
            var height = 90;

      			var shotId = document.getElementById("pass" + i + "-" + circle.teamName + "-" + circle.playerName );
            var circleRadio = Math.ceil(shotId.getAttribute('r'));
            let tooltipX = self.$vuetify.breakpoint.mobile ? (window.innerWidth/2) - (widthTooltip/2): shotId.getBoundingClientRect().left + circleRadio - widthTooltip / 2
            let tooltipY = self.$vuetify.breakpoint.mobile ? d3.select(`#${self.rootElementId}`).node().getBoundingClientRect().top + window.scrollY + (height/2) : (shotId.getBoundingClientRect().top + window.scrollY +  circleRadio - height - 50)

      			d3.select("body > .tooltip")
              .style("width", widthTooltip + "px")
              .style("height", height + "px")
      				.style("left", tooltipX + "px")
      				.style("top", tooltipY + "px")
      				.style("display", "inline-block")
              .style("font-size", "14px")
      				.html("<b>" + circle.game + "</b></br>" +
                    textLine2 + "</br>" +
                    textLine3 + "</br>" +
                    textLine4);
      		})
      		.on('mouseout',function() {
            d3.select(this)
              .transition()
              .duration(200)
              .attr('r', radius);
      			d3.select("body > .tooltip").style("display", "none");
      		});
      },
      drawXTLine(svg, chartSizeRate, segment, x0, y0, x1, y1, width, xt, tooltip, strokeColor = "black", opacity = 1, dashed = false) {
        var self = this;
    		svg.append("line")
         .attr("x1", x0)
         .attr("y1", y0)
         .attr("x2", x1)
         .attr("y2", y1)
         .attr('id', "pass" + segment.numberOfPlay + "-" + segment.teamName)
         .attr("stroke-width", 0.002 * width)
         .style("stroke-dasharray", 3 * chartSizeRate) //dashed array for line
    		 .style("opacity", 1)
         .attr("stroke", self.colorScale(0.4, 0, xt > 0.4 ? 0.4 : xt, 100))
      },
      drawCarryAndPassLine(svg, chartSizeRate, segment, sx0, sy0, sx1, sy1, sx2, sy2, width, xt) {
        svg.append("line")
          .attr("x1", sx1)
          .attr("y1", sy1)
          .attr("x2", sx2)
          .attr("y2", sy2)
          .attr('id', "pass" + segment.numberOfPlay + "-" + segment.teamName)
          .attr("stroke-width", 0.002 * width)
          .attr("stroke", this.colorScale(0.4, 0, xt > 0.4 ? 0.4 : xt, 100))
          .style("opacity", 1);

          svg.append("line")
            .attr("x1", sx0)
            .attr("y1", sy0)
            .attr("x2", sx1)
            .attr("y2", sy1)
            .attr('id', "pass" + segment.numberOfPlay + "-" + segment.teamName)
            .attr("stroke-width", 0.002 * width)
            .attr("stroke", this.colorScale(0.4, 0, xt > 0.4 ? 0.4 : xt, 100))
            .style("opacity", 1)
            .style("stroke-dasharray", 3 * chartSizeRate);

      },
      renderXTCircles(svg, chartSizeRate, chartWidth, chartHeight, circles, teamColor, strokeColor, tooltip, isPlayer) {

        let x = d3.scale.linear().domain([0 - (100 * 0.093), 100 + (100 * 0.1)]).range([chartWidth, 0]);
        let y = d3.scale.linear().domain([0 - (100 * 0.105), 100 + (100 * 0.105)]).range([chartHeight, 0]);

        let radius = d3.scale.linear().domain([0, 1]).range([chartWidth * 0.0055, chartWidth * 0.024]);
        for (let i in circles) {
          let sx;
          let sy;
          if (isPlayer) {
            sx = x(circles[i].y);
            sy = y(2 * circles[i].x - 100);
          } else if (circles[i].xT) {
            sx = circles[i].homeTeam ? x(circles[i].x) : x(100 - circles[i].x);
            sy = circles[i].homeTeam ? y(100 - circles[i].y) : y(circles[i].y);
          } else {
            sx = circles[i].homeTeam ? x(circles[i].x) : x(100 - circles[i].x);
            sy = circles[i].homeTeam ? y(100 - circles[i].y) : y(circles[i].y);
          }
          this.drawXTCircle(svg, chartSizeRate, chartWidth, sx, sy, circles[i], i, radius(circles[i].xT), circles[i].xT, tooltip);
        }
      },
      getPlayersWithHighestXT(shots) {
        // Group players by name
        const sumXTShots = shots.reduce(function(acc, val){
          var o = acc.filter(function(obj){
              return obj.player === val.player;
          }).pop() || {player: val.player, xT: 0};

          o.xT += val.xT;
          const index = acc.findIndex(p => p.player === o.player)
          if (index === -1)
            acc.push(o);
          else
            acc[index] = o;
          return acc;
        },[]);
        // Sort, take out players with xT < 0.1 and return first three
        return sumXTShots.sort((a, b) => b.xT - a.xT).filter(p => p.xT >= 0.05).slice(0, 3);
      },
      printGameLegend(svg, pitchSize, pitchHeight, margin, homeTeamColor, awayTeamColor, gameData) {
        let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
        let secondHomeTeamColor = this.homeBorderColor;
        let secondAwayTeamColor = this.awayBorderColor;

        let rectHome = this.drawRectangle(svg,
          0.045 * pitchSize + margin.left,
          margin.top - 3,
          16,
          (0.075 * pitchHeight) + 5,
          homeTeamColor.replace("0x", "#"), 0.9, "gameLegend1");
                  rectHome
        .style("border-radius",'3px')
        .style("border",`1px solid ${secondHomeTeamColor}`)
        .style("opacity",0.95)
        .style("stroke-width",1)
        .style("stroke",`${secondHomeTeamColor}`)
        .attr("rx",3)
        .attr("ry",3)

        this.drawImageBase64(svg,
          0.07 * pitchSize + margin.left,
          0.03 * pitchHeight + margin.top,
          0.05 * pitchSize,
          0.05 * pitchHeight,
          'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + gameData.homeTeamId + '.png','homeCrest');
        this.writeText(svg,
          0.115 * pitchSize + margin.left,
          0.04 * pitchHeight + margin.top,
          "start",
          (0.022 * pitchSize) + "px",
          `${ this.homeTeamName } • ${ gameData.homeTeamScore }`,textColor);

        let rectAway = this.drawRectangle(svg,
          0.935 * pitchSize + margin.left,
          margin.top - 3,
          16,
          (0.075 * pitchHeight) + 5,
          awayTeamColor.replace("0x", "#"), 0.9, "gameLegend1");
        rectAway
        .style("border-radius",'3px')
        .style("border",`1px solid ${secondAwayTeamColor}`)
        .style("opacity",0.95)
        .style("stroke-width",1)
        .style("stroke",`${secondAwayTeamColor}`)
        .attr("rx",3)
        .attr("ry",3)
        this.drawImageBase64(svg,
          0.885 * pitchSize + margin.left,
          0.03 * pitchHeight + margin.top,
          0.05 * pitchSize,
          0.05 * pitchHeight,
          'https://driblab-pro-images.s3.us-east-2.amazonaws.com/img/crests/' + gameData.awayTeamId + '.png','awayCrest');
        this.writeText(svg,
          0.89 * pitchSize + margin.left,
          0.04 * pitchHeight + margin.top,
          "end",
          (0.022 * pitchSize) + "px",
          `${ gameData.awayTeamScore } • ${ this.awayTeamName }`,textColor);
      },
      printPlayerXTLegend(svg, pitchWidth, margin, shots, seasons, minXT, typeOfPlay, playerName, language = "en") {
        let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
        const xT = shots.map(shot => shot.xT).reduce((a, b) => a + b, 0);
        const playerShots = shots.length;
        const goals = shots.length === 0 ? 0 : shots.filter(d => d.type === "GOAL").length;
        const games = seasons.map(s => s.gamesPlayed).reduce((a, b) => a + b, 0);
        const minutes = this.minsPlayed > 0 ? this.minsPlayed : seasons.map(s => s.minutesPlayed).reduce((a, b) => a + b, 0);
        const numberOfSeasons = seasons.length - 1;
        const rectangleSize = 0.16 * pitchWidth + numberOfSeasons * 0.023 * pitchWidth;
        let xTDecimals = 1;

        if(this.gameId == 0){
          this.writeText(svg, pitchWidth * 0.09 + margin.left, 0.045 * pitchWidth + margin.top, "start", (0.019 * pitchWidth) + "px",
          `${ Dictionary[this.language]["game"]["gameThreat"]["games"] }: ${ games } · ${ Dictionary[this.language]["game"]["gameThreat"]["minutes"] }: ${ minutes }`,
          textColor, "playerGamesAndMinutes");
        }else{
          xTDecimals = 2;
        }

        this.writeText(svg, pitchWidth * 0.92 + margin.left,
          0.045 * pitchWidth + margin.top,
          "end",
          (0.019 * pitchWidth) + "px",
          `${ playerShots } ${ Dictionary[this.language]["game"]["gameThreat"]["legendPlays"] } · ${ playerShots > 0 ? (xT / playerShots).toFixed(xTDecimals+1) : 0 } xT ${ Dictionary[this.language]["game"]["gameThreat"]["legendPerPlay"] }`, textColor, "playerShotsAndXG");

        this.drawRectangle(svg,
          (pitchWidth / 2 - 0.45 * pitchWidth / 2) + margin.left, // x
          pitchWidth * 0.58 - numberOfSeasons * 0.023 * pitchWidth, // y
          0.45 * pitchWidth, // width
          rectangleSize, // height
          "black",
          0.85,
          "xgPlayerLegendRect");
        this.writeText(svg, pitchWidth * 0.505 + margin.left, 0.6 * pitchWidth + margin.top - numberOfSeasons * 0.025 * pitchWidth, "middle", (0.026 * pitchWidth) + "px", playerName, "white", "playerXGName");

        svg.selectAll(".leagueAlias")
          .transition()
          .duration(200)
          .style("font-size", "0px");

        if(this.gameId == 0 && this.gameName == ""){
          //Print season's names
          svg.selectAll(".leagueAlias")
            .data(seasons)
            .enter()
            .append("text")
            .attr("class", "leagueAlias")
            .attr("x", pitchWidth / 2 + margin.left)
            .attr("y", (d, i) => i * 0.025 * pitchWidth + 0.65 * pitchWidth + margin.top - numberOfSeasons * 0.025 * pitchWidth)
            .attr("text-anchor", "middle")
            .attr("dy", "0.8em")
            .attr("font-weight", 500)
            .style("font-size", (0.021 * pitchWidth) + "px")
            .style("font-family", this.fontFamily)
            .style("fill", "white")
            .text(d => d.seasonName);
        }else{
          //Print game name
          this.writeText(svg,
            pitchWidth / 2 + margin.left,
            0.65 * pitchWidth + margin.top,
            "middle",
            (0.021 * pitchWidth) + "px",
            this.gameName,
            "white",
            "gameName"
          );
        }

        this.writeText(svg, pitchWidth * 0.505 + margin.left, 0.7 * pitchWidth + margin.top, "middle", (0.023 * pitchWidth) + "px",
        `${ xT.toFixed(xTDecimals) } ${ Dictionary[this.language]["game"]["gameThreat"]["xT"] } ·  ${ (xT / minutes * 90.0).toFixed(xTDecimals+1) } ${ Dictionary[this.language]["game"]["gameThreat"]["xTPer90"] }`, "white", "playerXGandGoals");

        this.writeText(svg, pitchWidth * 0.183 + margin.left, 0.58 * pitchWidth + margin.top, "middle", (0.014 * pitchWidth) + "px", Dictionary[this.language]["game"]["gameThreat"]["legendPass"],textColor);
        this.renderXTPassSegments(svg, 1, pitchWidth, 0.54 * pitchWidth,
          [{"x_start":38,"y_start":95,"x":38,"y":95,"x_end":38,"y_end":82,"xT":0,"type":"PASS","carry":true,"homeTeam":true}]
          , "#0000FF", "", this.type === "player", true);

        this.writeText(svg, pitchWidth * 0.183 + margin.left, 0.62 * pitchWidth + margin.top, "middle", (0.014 * pitchWidth) + "px", Dictionary[this.language]["game"]["gameThreat"]["legendCarryAndAction"],textColor);
        this.renderXTPassSegments(svg, 1, pitchWidth, 0.525 * pitchWidth,
          [{"x_start":31,"y_start":95,"x":31,"y":82,"x_end":31,"y_end":82,"xT":0,"type":"MISSED_SHOT","carry":true,"homeTeam":true}]
          , "#0000FF", "", this.type === "player", true);
        this.drawCircle(svg,
          pitchWidth * 0.234 + margin.left,
          0.645 * pitchWidth + margin.top,
          8 * pitchWidth * 0.001,
          "#28e628",
          "#28e628", "black", "xtLegendCircle");

        this.writeText(svg, pitchWidth * 0.183 + margin.left, 0.66 * pitchWidth + margin.top, "middle", (0.014 * pitchWidth) + "px", Dictionary[this.language]["game"]["gameThreat"]["legendCarryAndPass"],textColor);
        this.renderXTPassSegments(svg, 1, pitchWidth, 0.525 * pitchWidth,
          [{"x_start":26,"y_start":95,"x":26,"y":89,"x_end":26,"y_end":82,"xT":0,"type":"PASS","carry":true,"homeTeam":true}]
          , "#0000FF", "", this.type === "player", true);
        this.drawCircle(svg,
          pitchWidth * 0.18 + margin.left,
          0.688 * pitchWidth + margin.top,
          8 * pitchWidth * 0.001,
          "#28e628",
          "#28e628", "black", "xtLegendCircle");

        this.writeText(svg, pitchWidth * 0.092 + margin.left, 0.71 * pitchWidth + margin.top, "start", (0.014 * pitchWidth) + "px", Dictionary[this.language]["game"]["gameThreat"]["legendOnlyShowingPart1"],textColor);
        this.writeText(svg, pitchWidth * 0.092 + margin.left, 0.73 * pitchWidth + margin.top, "start", (0.014 * pitchWidth) + "px", Dictionary[this.language]["game"]["gameThreat"]["legendOnlyShowingPart2"] + minXT,textColor);
      },
      printGameXTLegend(svg, pitchWidth, margin, shots, minXT, typeOfPlay) {
        let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
        let homeShots = shots.filter(s => s.homeTeam);
        let awayShots = shots.filter(s => !s.homeTeam);

        let homeOwnGoals = homeShots.length === 0 ? 0 : homeShots.filter(d => d.ownGoal).length;
        let awayOwnGoals = awayShots.length === 0 ? 0 : awayShots.filter(d => d.ownGoal).length;

        let homeGoals = homeShots.length === 0 ? 0 : homeShots.filter(d => d.type === "GOAL").length - homeOwnGoals + awayOwnGoals;
        let awayGoals = awayShots.length === 0 ? 0 : awayShots.filter(d => d.type === "GOAL").length - awayOwnGoals + homeOwnGoals;

        let homeXT = homeShots.map(shot => shot.xT).reduce((a, b) => a + b, 0);
        let awayXT = awayShots.map(shot => shot.xT).reduce((a, b) => a + b, 0);

        const rectangleSize = 0.28 * pitchWidth;

        this.drawRectangle(svg,
          (pitchWidth / 2 - rectangleSize / 2) + margin.left,
          margin.top,
          rectangleSize,
          rectangleSize * 0.35,
          "black",
          0.8,
          "xtLegendRect");
        this.writeText(svg, pitchWidth / 2 + margin.left, 0.012 * pitchWidth + margin.top, "middle", (0.017 * pitchWidth) + "px", Dictionary[this.language]["game"]["gameThreat"]["legendSumOfXT"]);
        this.writeText(svg, pitchWidth / 2 + margin.left, 0.0525 * pitchWidth + margin.top, "middle", (0.04 * pitchWidth) + "px", `${ homeXT.toFixed(1) } • ${awayXT.toFixed(1)}`);

        let homeXTPlayers = this.getPlayersWithHighestXT(homeShots);
        let awayXTPlayers = this.getPlayersWithHighestXT(awayShots);

        homeXTPlayers.forEach((player, i) => {
          this.writeText(svg, pitchWidth * 0.495 + margin.left, (0.108 + (0.024 * i)) * pitchWidth + margin.top, "end", (0.018 * pitchWidth) + "px", `${player.player} · ${player.xT.toFixed(2)}`,textColor);
        });

        awayXTPlayers.forEach((player, i) => {
          this.writeText(svg, pitchWidth * 0.51 + margin.left, (0.108 + (0.024 * i)) * pitchWidth + margin.top, "start", (0.018 * pitchWidth) + "px", `${player.xT.toFixed(2)} · ${player.player}`,textColor);
        });

        let homePlaysLength = homeShots.length === 0 ? 0 : homeShots.filter(p => p.xT >= minXT).length;
        let awayPlaysLength = awayShots.length === 0 ? 0 : awayShots.filter(p => p.xT >= minXT).length;

        let homeXTPerPlayFiltered = homePlaysLength === 0 ? 0 : homeShots.filter(p => p.xT >= minXT).map(p => p.xT).reduce((a, b) => a + b, 0) / homePlaysLength;
        let awayXTPerPlayFiltered = awayPlaysLength === 0 ? 0 : awayShots.filter(p => p.xT >= minXT).map(p => p.xT).reduce((a, b) => a + b, 0) / awayPlaysLength;

        this.writeText(svg, pitchWidth * 0.495 + margin.left, 0.59 * pitchWidth + margin.top, "end", (0.016 * pitchWidth) + "px", `${ homePlaysLength } ${ Dictionary[this.language]["game"]["gameThreat"]["legendPlays"] } · ${  homeXTPerPlayFiltered.toFixed(2)} ${ Dictionary[this.language]["game"]["gameThreat"]["legendPerPlay"] }`,textColor);
        this.writeText(svg, pitchWidth * 0.51 + margin.left, 0.59 * pitchWidth + margin.top, "start", (0.016 * pitchWidth) + "px", `${ awayPlaysLength } ${ Dictionary[this.language]["game"]["gameThreat"]["legendPlays"] } · ${ awayXTPerPlayFiltered.toFixed(2)} ${ Dictionary[this.language]["game"]["gameThreat"]["legendPerPlay"] }`,textColor);

        this.renderXTPassSegments(svg, 1, pitchWidth, 0.525 * pitchWidth,
          [{"x_start":99,"y_start":135,"x":99,"y":135,"x_end":91,"y_end":135,"xT":0,"type":"PASS","carry":true,"homeTeam":true}]
          , "#0000FF", "", this.type === "player", true);

        this.writeText(svg, pitchWidth * 0.175 + margin.left, 0.625 * pitchWidth + margin.top, "start", (0.014 * pitchWidth) + "px", Dictionary[this.language]["game"]["gameThreat"]["legendPass"],textColor);

        this.renderXTPassSegments(svg, 1, pitchWidth, 0.525 * pitchWidth,
          [{"x_start":99.5,"y_start":140,"x":91,"y":140,"x_end":91,"y_end":140,"xT":0,"type":"MISSED_SHOT","carry":true,"homeTeam":true}]
          , "#0000FF", "", this.type === "player", true);
        this.drawCircle(svg,
          pitchWidth * 0.16 + margin.left,
          0.6533 * pitchWidth + margin.top,
          8 * pitchWidth * 0.001,
          "#28e628",
          "#28e628", "black", "xtLegendCircle");
        this.writeText(svg, pitchWidth * 0.175 + margin.left, 0.645 * pitchWidth + margin.top, "start", (0.014 * pitchWidth) + "px", Dictionary[this.language]["game"]["gameThreat"]["legendCarryAndAction"],textColor);

        this.renderXTPassSegments(svg, 1, pitchWidth, 0.525 * pitchWidth,
          [{"x_start":72,"y_start":135,"x":67,"y":135,"x_end":62,"y_end":135,"xT":0,"type":"PASS","carry":true,"homeTeam":true}]
          , "#0000FF", "", this.type === "player", true);
        this.drawCircle(svg,
          pitchWidth * 0.36 + margin.left,
          0.6315 * pitchWidth + margin.top,
          8 * pitchWidth * 0.001,
          "#28e628",
          "#28e628", "black", "xtLegendCircle");
        this.writeText(svg, pitchWidth * 0.415 + margin.left, 0.625 * pitchWidth + margin.top, "start", (0.014 * pitchWidth) + "px", Dictionary[this.language]["game"]["gameThreat"]["legendCarryAndPass"],textColor);

        this.writeText(svg, pitchWidth * 0.92 + margin.left, 0.625 * pitchWidth + margin.top, "end", (0.014 * pitchWidth) + "px",
          typeOfPlay.length === 1 ? typeOfPlay[0] === "Set Piece" ? Dictionary[this.language]["game"]["gameThreat"]["legendOnlyShowingSetPiece"] + minXT : Dictionary[this.language]["game"]["gameThreat"]["legendOnlyShowingOpenPlay"] + minXT : Dictionary[this.language]["game"]["gameThreat"]["legendOnlyShowing"] + minXT,textColor);

      },
      renderBoxes(svg, svgWidth, pitchHeight, margin, shots, language = 'en') {
        let textColor = this.currentUserFieldColor === 'white' ? 'black' : 'white';
        const shotLegend = language === 'en' ? 'plays' : 'jugadas';

        const goalArea = shots.filter(s => s.x_start >= 94.2 && s.y >= 36.8 && s.y <= 63.2).length;
        const boxRight = shots.filter(s => s.x_start >= 83 && s.y >= 21.1 && s.y < 36.8).length;
        const boxCenter = shots.filter(s => s.x_start >= 83 && s.x_start < 94.2 && s.y >= 36.8 && s.y <= 63.2).length;
        const boxLeft = shots.filter(s => s.x_start >= 83 && s.y > 63.2 && s.y < 78.9).length;
        //Out Of Box
        const outBoxDeepRight = shots.filter(s => s.x_start >= 83 && s.y < 21.1).length;
        const outBoxDeepLeft = shots.filter(s => s.x_start >= 83 && s.y > 78.9).length;
        const outBoxRight = shots.filter(s => s.x_start >= 65 && s.x_start < 83 && s.y < 21.1).length;
        const outBoxCentreRight = shots.filter(s => s.x_start >= 65 && s.x_start < 83 && s.y >= 21.1 && s.y <= 35.5).length;
        const outBoxCentre = shots.filter(s => s.x_start >= 65 && s.x_start < 83 && s.y >= 35.6 && s.y <= 64.4).length;
        const outBoxCentreLeft = shots.filter(s => s.x_start >= 65 && s.x_start < 83 && s.y >= 64.5 && s.y <= 78.9).length;
        const outBoxLeft = shots.filter(s => s.x_start >= 65 && s.x_start < 83 && s.y > 78.9).length;
        //+35
        const right35 = shots.filter(s => s.x_start >= 50 && s.x_start < 65 && s.y < 21.1).length;
        const centreRight35 = shots.filter(s => s.x_start >= 50 && s.x_start < 65 && s.y >= 21.1 && s.y <= 35.5).length;
        const centre35 = shots.filter(s => s.x_start >= 50 && s.x_start < 65 && s.y >= 35.6 && s.y <= 64.4).length;
        const centreLeft35 = shots.filter(s => s.x_start >= 50 && s.x_start < 65 && s.y >= 64.5 && s.y <= 78.9).length;
        const left35 = shots.filter(s => s.x_start >= 50 && s.x_start < 65 && s.y > 78.9).length;

        var shadowScale = d3.scale.linear().domain([0, shots.length]).range([0.1, 0.75]);

        if (outBoxDeepLeft > 0.5) {
          this.drawRectangle(svg, 0.094 * svgWidth, 0.105 * pitchHeight, 0.155 * svgWidth, 0.25 * pitchHeight, "red", shadowScale(outBoxDeepLeft), "gameLegend1");
          this.writeText(svg, 0.177 * svgWidth, 0.2 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxDeepLeft / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.177 * svgWidth, 0.23 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxDeepLeft) } ${ shotLegend })`,textColor);
        }
        if (outBoxLeft > 0.5) {
          this.drawRectangle(svg, 0.094 * svgWidth, 0.365 * pitchHeight, 0.155 * svgWidth, 0.295 * pitchHeight, "red", shadowScale(outBoxLeft), "gameLegend1");
          this.writeText(svg, 0.177 * svgWidth, 0.46 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxLeft / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.177 * svgWidth, 0.49 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxLeft) } ${ shotLegend })`,textColor);
        }
        if (left35 > 0.5) {
          this.drawRectangle(svg, 0.094 * svgWidth, 0.665 * pitchHeight, 0.155 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(left35), "gameLegend1");
          this.writeText(svg, 0.177 * svgWidth, 0.735 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(left35 / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.177 * svgWidth, 0.765 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(left35) } ${ shotLegend })`,textColor);
        }
        if (outBoxDeepRight > 0.5) {
          this.drawRectangle(svg, 0.76 * svgWidth, 0.105 * pitchHeight, 0.155 * svgWidth, 0.25 * pitchHeight, "red", shadowScale(outBoxDeepRight), "gameLegend1");
          this.writeText(svg, 0.842 * svgWidth, 0.2 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxDeepRight / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.842 * svgWidth, 0.23 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxDeepRight) } ${ shotLegend })`,textColor);
        }
        if (outBoxRight > 0.5) {
          this.drawRectangle(svg, 0.76 * svgWidth, 0.365 * pitchHeight, 0.155 * svgWidth, 0.295 * pitchHeight, "red", shadowScale(outBoxRight), "gameLegend1");
          this.writeText(svg, 0.842 * svgWidth, 0.46 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxRight / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.842 * svgWidth, 0.49 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxRight) } ${ shotLegend })`,textColor);
        }
        if (right35 > 0.5) {
          this.drawRectangle(svg, 0.76 * svgWidth, 0.665 * pitchHeight, 0.155 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(right35), "gameLegend1");
          this.writeText(svg, 0.842 * svgWidth, 0.735 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(right35 / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.842 * svgWidth, 0.765 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(right35) } ${ shotLegend })`,textColor);
        }

        if (outBoxCentreLeft > 0.5) {
          this.drawRectangle(svg, 0.254 * svgWidth, 0.365 * pitchHeight, 0.13 * svgWidth, 0.295 * pitchHeight, "red", shadowScale(outBoxCentreLeft), "gameLegend1");
          this.writeText(svg, 0.33 * svgWidth, 0.46 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxCentreLeft / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.33 * svgWidth, 0.49 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxCentreLeft) } ${ shotLegend })`,textColor);
        }
        if (outBoxCentre > 0.5) {
          this.drawRectangle(svg, 0.39 * svgWidth, 0.365 * pitchHeight, 0.23 * svgWidth, 0.295 * pitchHeight, "red", shadowScale(outBoxCentre), "gameLegend1");
          this.writeText(svg, 0.505 * svgWidth, 0.46 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxCentre / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.505 * svgWidth, 0.49 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxCentre) } ${ shotLegend })`,textColor);
        }
        if (outBoxCentreRight > 0.5) {
          this.drawRectangle(svg, 0.625 * svgWidth, 0.365 * pitchHeight, 0.13 * svgWidth, 0.295 * pitchHeight, "red", shadowScale(outBoxCentreRight), "gameLegend1");
          this.writeText(svg, 0.69 * svgWidth, 0.46 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(outBoxCentreRight / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.69 * svgWidth, 0.49 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(outBoxCentreRight) } ${ shotLegend })`,textColor);
        }

        if (centreLeft35 > 0.5) {
          this.drawRectangle(svg, 0.254 * svgWidth, 0.665 * pitchHeight, 0.13 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(centreLeft35), "gameLegend1");
          this.writeText(svg, 0.33 * svgWidth, 0.69 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(centreLeft35 / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.33 * svgWidth, 0.72 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(centreLeft35) } ${ shotLegend })`,textColor);
        }

        if (centre35 > 0.5) {
          this.drawRectangle(svg, 0.39 * svgWidth, 0.665 * pitchHeight, 0.23 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(centre35), "gameLegend1");
          this.writeText(svg, 0.505 * svgWidth, 0.72 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(centre35 / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.505 * svgWidth, 0.69 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(centre35) } ${ shotLegend })`,textColor);
        }
        if (centreRight35 > 0.5) {
          this.drawRectangle(svg, 0.625 * svgWidth, 0.665 * pitchHeight, 0.13 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(centreRight35), "gameLegend1");
          this.writeText(svg, 0.69 * svgWidth, 0.69 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(centreRight35 / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.69 * svgWidth, 0.72 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(centreRight35) } ${ shotLegend })`,textColor);
        }
        if (boxLeft > 0.5) {
          this.drawRectangle(svg, 0.265 * svgWidth, 0.105 * pitchHeight, 0.12 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(boxLeft), "gameLegend1");
          this.writeText(svg, 0.33 * svgWidth, 0.20 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(boxLeft / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.33 * svgWidth, 0.23 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(boxLeft) } ${ shotLegend })`,textColor);
        }
        if (boxRight > 0.5) {
          this.drawRectangle(svg, 0.625 * svgWidth, 0.105 * pitchHeight, 0.12 * svgWidth, 0.24 * pitchHeight, "red", shadowScale(boxRight), "gameLegend1");
          this.writeText(svg, 0.69 * svgWidth, 0.20 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(boxRight / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.69 * svgWidth, 0.23 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(boxRight) } ${ shotLegend })`,textColor);
        }
        if (goalArea > 0.5) {
          this.drawRectangle(svg, 0.4 * svgWidth, 0.105 * pitchHeight, 0.21 * svgWidth, 0.068 * pitchHeight, "red", shadowScale(goalArea), "gameLegend1");
          this.writeText(svg, 0.505 * svgWidth, 0.11 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(goalArea / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.505 * svgWidth, 0.14 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(goalArea) } ${ shotLegend })`,textColor);
        }
        if (boxCenter > 0.5) {
          this.drawRectangle(svg, 0.39 * svgWidth, 0.19 * pitchHeight, 0.23 * svgWidth, 0.155 * pitchHeight, "red", shadowScale(boxCenter), "gameLegend1");
          this.writeText(svg, 0.505 * svgWidth, 0.2 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `${ Math.round(boxCenter / shots.length * 100) } %`,textColor);
          this.writeText(svg, 0.505 * svgWidth, 0.23 * pitchHeight, "middle", (0.018 * svgWidth) + "px", `(${ Math.round(boxCenter) } ${ shotLegend })`,textColor);
        }

      },
      onResize(){
        let elto =document.getElementById(this.rootElementId);
        if(elto){
          this.$emit("sizeChange", {height:elto.clientHeight,width:elto.clientWidth});
        }
      }
    },
    destroyed() {
      delete this.observer;
    },
  }

</script>



// WEBPACK FOOTER //
// src/views/base/XTChart.vue